import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppSelector } from 'core/store/hooks';
import { selectThemes } from 'core/selectors/product/theme';
import { EntityTypes } from 'constants/entities';
import { useSave } from 'core/hooks/useSave';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import classNames from 'classnames';
import { applyTheme } from 'utils/applyTheme';
import { useNavigate } from 'react-router-dom';
import { ThemeConfig } from 'types/domain/theme';
import { ThemePicker } from './ThemePicker';

interface ThemeSelectProps {
  courseNodeId: number;
  className?: string;
}

/** A module's or course's theme: the theme picker, saved on the course node and applied straight away. */
const ThemeSelect: React.FC<ThemeSelectProps> = ({ courseNodeId, className }) => {
  const themes = useSelector(selectThemes);
  const courseNode = useAppSelector(state => selectCourseNode(state, courseNodeId));
  const save = useSave(EntityTypes.COURSE_NODE);
  const navigate = useNavigate();

  const selectedId = courseNode?.themeId ?? null;

  const handleChange = useCallback(
    (val: number | null) => {
      save({ id: courseNodeId, themeId: val || null });
      const theme = themes.find(t => t.id === val);
      applyTheme(theme ? ((theme as any).config as ThemeConfig) : null);
    },
    [courseNodeId, save, themes],
  );

  return (
    <div className={classNames('flex flex-col gap-2 font-adm', className)}>
      <ThemePicker value={selectedId} onChange={handleChange} />

      <p className="mt-1 text-[13px] leading-snug text-adm-ink2">
        A theme is a saved colour and logo pair. Set up once under{' '}
        <button
          type="button"
          onClick={() => navigate('/theme')}
          className="font-medium text-adm-accent underline underline-offset-2"
        >
          Settings
        </button>
        , then reused across modules.
      </p>
    </div>
  );
};

export default ThemeSelect;
